import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Check, X, MapPin, Instagram, Clock, Calendar } from "lucide-react";

const isDev = process.env.NODE_ENV === "development";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const weekdayLabel = (n) => (n !== null && n !== undefined ? WEEKDAYS_ES[Number(n)] ?? "" : "");
const markerWeekdays = (m) => {
  if (Array.isArray(m?.weekdays) && m.weekdays.length > 0) return m.weekdays.map(Number);
  if (m?.weekday !== null && m?.weekday !== undefined) return [Number(m.weekday)];
  return [];
};
const formatWeekdaysLabel = (weekdays) => {
  const list = (weekdays || []).map(Number).filter((n) => Number.isFinite(n));
  if (list.length === 0) return "";
  if (list.length === 7) return "Todos los días";
  const sorted = [...list].sort((a, b) => a - b);
  const names = sorted.map((n) => WEEKDAYS_ES[n]).filter(Boolean);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} y ${names[1]}`;
  return names.slice(0, -1).join(", ") + " y " + names[names.length - 1];
};

export function PendingApprovalsDialog({ open, onClose, pendingMarkers, onAction, apiPath = "markers", entityLabel = "punto" }) {
  const [processingId, setProcessingId] = useState(null);

  const capLabel = entityLabel[0].toUpperCase() + entityLabel.slice(1);

  const handleApprove = async (markerId) => {
    setProcessingId(markerId);
    try {
      await axios.post(`${API}/${apiPath}/${markerId}/approve`);
      toast.success(`${capLabel} aprobado y publicado en el mapa`);
      onAction();
    } catch (error) {
      if (isDev) console.error("Approve error:", error);
      toast.error(error.response?.data?.detail || `Error al aprobar el ${entityLabel}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (markerId) => {
    setProcessingId(markerId);
    try {
      await axios.delete(`${API}/${apiPath}/${markerId}`);
      toast.success(`${capLabel} rechazado y eliminado`);
      onAction();
    } catch (error) {
      if (isDev) console.error("Reject error:", error);
      toast.error(error.response?.data?.detail || `Error al rechazar el ${entityLabel}`);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl md:text-2xl flex items-center gap-2">
            <Clock size={22} className="text-[#FF5A5F]" />
            {capLabel}s por Aprobar
          </DialogTitle>
          <DialogDescription>
            {pendingMarkers.length === 0
              ? `No hay ${entityLabel}s pendientes de aprobación`
              : `${pendingMarkers.length} ${pendingMarkers.length === 1 ? `${entityLabel} espera` : `${entityLabel}s esperan`} tu revisión`}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {pendingMarkers.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="mx-auto mb-3 text-slate-300" size={48} />
              <p className="text-sm text-slate-500">
                Todo al día. Los nuevos {entityLabel}s que agregue la gente aparecerán acá.
              </p>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {pendingMarkers.map((marker) => (
                <div
                  key={marker.id}
                  data-testid={`pending-marker-${marker.id}`}
                  className="bg-orange-50/50 border border-orange-200 rounded-xl p-4"
                >
                  <div className="mb-3">
                    <h3 className="font-semibold text-slate-900 text-base mb-1">
                      {marker.name}
                    </h3>
                    {(marker.date || marker.time || marker.recurrence === "weekly") && (
                      <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-purple-700">
                        <Calendar size={13} />
                        <span>
                          {marker.recurrence === "weekly"
                            ? formatWeekdaysLabel(markerWeekdays(marker))
                            : marker.date}
                        </span>
                        {marker.time && (
                          <>
                            <span className="text-slate-300">•</span>
                            <Clock size={12} />
                            <span>{marker.time}</span>
                          </>
                        )}
                      </div>
                    )}
                    {marker.description && (
                      <p className="text-sm text-slate-600 mb-2 leading-relaxed">
                        {marker.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} />
                        {marker.lat.toFixed(4)}, {marker.lng.toFixed(4)}
                      </span>
                      {marker.instagramUrl && (
                        <a
                          href={marker.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#FF5A5F] hover:underline"
                        >
                          <Instagram size={12} />
                          Ver perfil
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-marker-${marker.id}`}
                      onClick={() => handleApprove(marker.id)}
                      disabled={processingId === marker.id}
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white h-9"
                    >
                      <Check size={16} className="mr-1" />
                      Aprobar
                    </Button>
                    <Button
                      data-testid={`reject-marker-${marker.id}`}
                      onClick={() => handleReject(marker.id)}
                      disabled={processingId === marker.id}
                      size="sm"
                      variant="outline"
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 border-red-200 h-9"
                    >
                      <X size={16} className="mr-1" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end pt-2">
          <Button
            onClick={onClose}
            variant="outline"
            className="rounded-full px-6"
          >
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
